import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Settings2, Download, AlertTriangle, Filter, ShieldAlert } from "lucide-react";

const ORIGIN_TONE = {
  seed: "bg-slate-200 text-slate-700",
  user: "bg-emerald-100 text-emerald-800",
  unknown: "bg-amber-100 text-amber-800",
};

export default function AdminDataInventory() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [originFilter, setOriginFilter] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [i, s] = await Promise.all([api.get("/admin/data-inventory"), api.get("/admin/settings")]);
    setData(i.data); setSettings(s.data);
  };
  useEffect(() => { load(); }, []);

  const toggleAllMarkets = async (v) => {
    const r = await api.put("/admin/settings", { show_research_preview_markets: v });
    setSettings(r.data);
    toast.success(v ? "Research-preview markets are visible" : "Research-preview markets hidden — SATX only");
  };

  const key = (coll, id) => `${coll}::${id}`;
  const toggle = (coll, id) => {
    const k = key(coll, id);
    const s = new Set(selected);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSelected(s);
  };
  const toggleGroupAll = (rows, coll) => {
    const s = new Set(selected);
    const all = rows.every(r => s.has(key(coll, r.id)));
    rows.forEach(r => { const k = key(coll, r.id); if (all) s.delete(k); else s.add(k); });
    setSelected(s);
  };

  const exportUrl = (fmt) => `${API}/admin/data-inventory/export?format=${fmt}`;
  const download = async (fmt) => {
    const res = await fetch(exportUrl(fmt), {
      headers: { Authorization: `Bearer ${localStorage.getItem("sahoi_token") || ""}` }
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `throughline-data-inventory.${fmt === "csv" ? "csv" : "json"}`; a.click();
    URL.revokeObjectURL(url);
  };

  const doPurge = async () => {
    if (typed !== "PURGE") return;
    setBusy(true);
    try {
      const items = [...selected].map(k => { const [collection, id] = k.split("::"); return { collection, id }; });
      const r = await api.post("/admin/purge", { items, confirm: "PURGE" });
      const blocked = (r.data.purged || []).filter(x => x.status === "blocked");
      toast.success(`Purged ${r.data.count} record${r.data.count === 1 ? "" : "s"}${blocked.length ? ` · ${blocked.length} blocked` : ""}`);
      setSelected(new Set());
      setTyped(""); setConfirmOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge failed");
    } finally { setBusy(false); }
  };

  const filteredGroups = useMemo(() => {
    if (!data) return [];
    const ql = q.toLowerCase();
    return data.groups.map(g => ({
      ...g,
      rows: g.rows.filter(r =>
        (originFilter === "all" || r.seed_origin === originFilter) &&
        (!flaggedOnly || r.flags.length > 0) &&
        (!ql || (r.label || "").toLowerCase().includes(ql) || (r.id || "").toLowerCase().includes(ql))
      ),
    }));
  }, [data, q, originFilter, flaggedOnly]);

  if (!data || !settings) return <div className="text-sm text-slate-500">Loading inventory…</div>;

  const sel = selected.size;

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2"><Settings2 className="w-3.5 h-3.5" /> Admin</div>
        <h1 className="text-3xl font-bold">Data Inventory &amp; Cleanup</h1>
        <p className="text-sm text-slate-500 mt-1">Review every record before pilot launch. Nothing is auto-deleted — you decide.</p>
      </div>

      {/* Settings */}
      <Card className="p-5 border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2"><Filter className="w-3.5 h-3.5" /> Market visibility</div>
            <div className="text-sm text-slate-800 mt-1">
              <span className="font-semibold">Show research-preview markets</span> · Austin · Houston · Dallas–Fort Worth
            </div>
            <div className="text-xs text-slate-500 mt-1">When off, only Greater San Antonio (Bexar · Comal · Guadalupe · Kendall) appears in matching, coverage, and the landing page.</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-widest font-semibold ${!settings.show_research_preview_markets ? "text-slate-900" : "text-slate-400"}`}>SATX only</span>
            <Switch checked={!!settings.show_research_preview_markets} onCheckedChange={toggleAllMarkets} data-testid="settings-preview-toggle" />
            <span className={`text-[10px] uppercase tracking-widest font-semibold ${settings.show_research_preview_markets ? "text-slate-900" : "text-slate-400"}`}>All markets</span>
          </div>
        </div>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="inventory-summary">
        <SummaryTile label="Total records" value={data.summary.total} />
        <SummaryTile label="Seed origin" value={data.summary.by_origin.seed || 0} tone="slate" />
        <SummaryTile label="User origin" value={data.summary.by_origin.user || 0} tone="emerald" />
        <SummaryTile label="Unknown origin" value={data.summary.by_origin.unknown || 0} tone="amber" />
        <SummaryTile label="Flagged test artifacts" value={data.summary.flagged} tone="rose" />
      </div>

      {/* Toolbar */}
      <Card className="p-4 border-slate-200 bg-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Input placeholder="Search by label or id…" value={q} onChange={e=>setQ(e.target.value)} className="max-w-md" data-testid="inventory-search" />
          <select value={originFilter} onChange={e=>setOriginFilter(e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-2 bg-white" data-testid="inventory-origin-filter">
            <option value="all">All origins</option>
            <option value="seed">Seed only</option>
            <option value="user">User only</option>
            <option value="unknown">Unknown</option>
          </select>
          <label className="flex items-center gap-1 text-xs cursor-pointer" data-testid="inventory-flagged-toggle">
            <input type="checkbox" checked={flaggedOnly} onChange={e=>setFlaggedOnly(e.target.checked)} /> Flagged only
          </label>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600" data-testid="inventory-selection-count">{sel} selected</span>
          <Button variant="outline" onClick={()=>download("csv")} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1"/>CSV</Button>
          <Button variant="outline" onClick={()=>download("json")} data-testid="export-json-btn"><Download className="w-4 h-4 mr-1"/>JSON</Button>
          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogTrigger asChild>
              <Button disabled={sel === 0} className="bg-rose-700 hover:bg-rose-800 disabled:opacity-40" data-testid="purge-open-btn">Purge {sel}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-rose-700"/>Confirm purge</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-sm text-slate-700">
                  This will permanently delete <strong>{sel}</strong> record{sel === 1 ? "" : "s"}. Property deletions cascade to their packets, underwritings and conversations. Program deletions cascade to underwritings; packets are unlinked (program_id blanked).
                </div>
                <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2">
                  <strong>Protected:</strong> the pilot property 3614 Versailles Dr and its critical facts cannot be deleted.
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Type PURGE to confirm</Label>
                  <Input value={typed} onChange={e=>setTyped(e.target.value)} className="font-mono" data-testid="purge-confirm-input" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={()=>{ setTyped(""); setConfirmOpen(false); }} className="flex-1">Cancel</Button>
                  <Button className="bg-rose-700 hover:bg-rose-800 flex-1 disabled:opacity-40" disabled={typed !== "PURGE" || busy} onClick={doPurge} data-testid="purge-confirm-btn">{busy ? "Purging…" : "Purge"}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </Card>

      {/* Groups */}
      {filteredGroups.map(g => (
        <Card key={g.collection} className="p-0 border-slate-200 bg-white overflow-hidden" data-testid={`inventory-group-${g.collection}`}>
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{g.collection}</div>
              <div className="text-base font-semibold">{g.label} ({g.rows.length}/{g.count})</div>
            </div>
            <Button size="sm" variant="outline" onClick={()=>toggleGroupAll(g.rows, g.collection)} data-testid={`select-all-${g.collection}`}>Select visible</Button>
          </div>
          {g.rows.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 italic">No rows match the filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="w-8 px-3 py-2"></th>
                  <th className="text-left px-3 py-2">Label</th>
                  <th className="text-left px-3 py-2">Origin</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Refs</th>
                  <th className="text-left px-3 py-2">Flags</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map(r => {
                  const k = key(g.collection, r.id);
                  const isSel = selected.has(k);
                  return (
                    <tr key={r.id} className={`border-b border-slate-100 last:border-b-0 ${isSel ? "bg-rose-50/40" : ""}`} data-testid={`inventory-row-${g.collection}-${r.id}`}>
                      <td className="px-3 py-2"><input type="checkbox" checked={isSel} onChange={()=>toggle(g.collection, r.id)} data-testid={`select-${g.collection}-${r.id}`} /></td>
                      <td className="px-3 py-2 max-w-md truncate"><span className="font-semibold">{r.label}</span><span className="ml-2 text-[10px] text-slate-400 font-mono">{(r.id||"").slice(0,8)}…</span></td>
                      <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${ORIGIN_TONE[r.seed_origin] || ORIGIN_TONE.unknown}`}>{r.seed_origin}</span></td>
                      <td className="px-3 py-2 text-xs text-slate-500 font-mono">{(r.created_at || "").slice(0,10)}</td>
                      <td className="px-3 py-2 text-xs text-slate-700">{r.reference_count > 0 ? `${r.reference_count} ref${r.reference_count === 1 ? "" : "s"}` : "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.flags.length === 0 ? <span className="text-slate-400">—</span> : (
                          <span className="inline-flex items-center gap-1 text-rose-700"><AlertTriangle className="w-3 h-3" />{r.flags.join(" · ")}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      ))}
    </div>
  );
}

function SummaryTile({ label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-900", emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-700" };
  return (
    <Card className="p-4 border-slate-200 bg-white">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold font-mono mt-1 ${tones[tone]}`}>{value}</div>
    </Card>
  );
}
